'use client';

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import store from 'src/redux/store';
import { useDebounce } from 'use-debounce';
import { useGetFilterSetByIdQuery, useGetFilterSetsQuery } from 'src/api/redux/filterSets';
import {
  GenericNodeComponent,
  GenericSelectorModal,
  ConnectorType,
} from '../shared';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faChevronDown,
  faBuilding,
  faUser,
  faFileSlash,
} from '@fortawesome/pro-solid-svg-icons';
import { stripHtml } from 'src/utils/stripHtml';

import {
  CodeSetItem,
  codeSetNodeConfig,
  codeSetModalConfig,
  transformCodeSet,
} from './config';

interface CodeSetsComponentProps {
  componentId: string;
  initialCodeSets?: CodeSetItem[];
  initialConnector?: ConnectorType;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

const CodeSetsComponent: React.FC<CodeSetsComponentProps> = ({
  componentId,
  initialCodeSets = [],
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  // Initialize state - ui_metadata provides pre-hydrated objects
  const [codeSets, setCodeSets] = useState<CodeSetItem[]>(initialCodeSets);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebounce(searchTerm, 300);
  const [expandedSection, setExpandedSection] = useState<
    'company' | 'personal' | null
  >('company');

  // Track if this is a new component from a placeholder that hasn't saved yet
  const [isNewFromPlaceholder, setIsNewFromPlaceholder] = useState(
    fromPlaceholder && initialCodeSets.length === 0
  );

  // Check if we need to hydrate from ID
  const needsHydration =
    initialCodeSets.length > 0 &&
    (typeof initialCodeSets[0] === 'string' ||
      typeof initialCodeSets[0] === 'number');

  const codeSetId = needsHydration ? String(initialCodeSets[0]) : null;

  const {
    data: hydratedData,
    isLoading: isHydrating,
    error,
  } = useGetFilterSetByIdQuery(
    { id: codeSetId as string },
    { skip: !needsHydration || !codeSetId }
  );

  // Fetch code sets for the modal
  const { data: filterSetsData, isLoading: isSearching } = useGetFilterSetsQuery({
    q: debouncedSearch || '',
    serializer: 'detailed',
  });

  // Filter to only CODE type and transform to CodeSetItem format
  const searchResults: CodeSetItem[] = useMemo(() => {
    const codeSetsFromApi =
      filterSetsData?.results?.filter((fs) => fs.type === 'CODE') || [];
    return codeSetsFromApi.map((fs) => transformCodeSet(fs as any));
  }, [filterSetsData?.results]);

  // Group code sets by scope for custom rendering
  const groupedCodeSets = useMemo(() => {
    const companyCodeSets = searchResults
      .filter((cs) => cs.scope === 'COMPANY')
      .sort((a, b) => a.label.localeCompare(b.label));

    const personalCodeSets = searchResults
      .filter((cs) => cs.scope !== 'COMPANY')
      .sort((a, b) => a.label.localeCompare(b.label));

    return { companyCodeSets, personalCodeSets };
  }, [searchResults]);

  // Hydrate the code set data when it loads
  useEffect(() => {
    if (needsHydration && hydratedData) {
      const transformed = transformCodeSet(hydratedData as any);
      setCodeSets([transformed]);
    }
  }, [needsHydration, hydratedData]);

  // Auto-open modal if starting with no code sets (new component)
  useEffect(() => {
    if (initialCodeSets.length === 0 && !isHydrating) {
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Sync when external items are added (slash menu merge)
  useEffect(() => {
    if (initialCodeSets.length > codeSets.length) {
      setCodeSets(initialCodeSets);
    }
  }, [initialCodeSets.length]);

  const handleSave = useCallback(
    (newCodeSets: CodeSetItem[], connector: ConnectorType) => {
      if (!newCodeSets || newCodeSets.length === 0) {
        onRemove(componentId);
        setShowModal(false);
        return;
      }

      const selectedCodeSet = newCodeSets[0];
      setCodeSets([selectedCodeSet]);

      // Clear the new-from-placeholder flag since we have data now
      setIsNewFromPlaceholder(false);

      // CodeSets always use 'ALL' connector (single selection), but we accept the param for type compatibility
      onUpdate(componentId, {
        type: 'code_set',
        value: selectedCodeSet.id,
        ui_metadata: [selectedCodeSet], // Store full object for hydration-free loading
        connector: connector || 'ALL',
      });

      setShowModal(false);
    },
    [componentId, onUpdate, onRemove]
  );

  // Handle modal close - if from placeholder with no data, remove component
  const handleModalClose = useCallback(() => {
    setShowModal(false);
    // If this was new from a placeholder and user closed without selecting, remove it
    if (isNewFromPlaceholder && codeSets.length === 0) {
      onRemove(componentId);
    }
  }, [isNewFromPlaceholder, codeSets.length, onRemove, componentId]);

  const handleRemoveItem = useCallback(() => {
    onRemove(componentId);
  }, [componentId, onRemove]);

  // Custom render function for grouped results (Company/Personal sections)
  const renderGroupedResults = useCallback(
    ({
      selectedItems,
      onSelect,
      isLoading,
      searchTerm: currentSearchTerm,
    }: {
      results: CodeSetItem[];
      selectedItems: CodeSetItem[];
      onSelect: (item: CodeSetItem) => void;
      onDeselect: (item: CodeSetItem) => void;
      isLoading: boolean;
      searchTerm: string;
    }) => {
      const hasResults =
        groupedCodeSets.companyCodeSets.length > 0 ||
        groupedCodeSets.personalCodeSets.length > 0;

      if (isLoading) {
        return (
          <div className="h-full flex items-center justify-center text-neutral-500 border border-neutral-200 rounded-lg bg-neutral-50/50">
            <div className="text-center animate-in fade-in duration-300">
              <div className="animate-pulse text-neutral-400">
                Loading code sets...
              </div>
            </div>
          </div>
        );
      }

      if (!hasResults) {
        return (
          <div className="h-full flex items-center justify-center text-neutral-500 border border-neutral-200 rounded-lg bg-neutral-50/50">
            <div className="text-center animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="flex flex-col items-center gap-3">
                <Icon
                  icon={faFileSlash}
                  className="w-12 h-12 text-neutral-300"
                />
                <div className="flex flex-col items-center gap-1">
                  <div className="text-sm">
                    {currentSearchTerm
                      ? `No code sets found for "${currentSearchTerm}"`
                      : 'No code sets available'}
                  </div>
                  {currentSearchTerm && (
                    <div className="text-xs text-neutral-400">
                      Try adjusting your search terms
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      const renderSection = (
        title: string,
        icon: typeof faBuilding,
        items: CodeSetItem[],
        sectionKey: 'company' | 'personal'
      ) => {
        if (items.length === 0) return null;

        return (
          <div key={sectionKey}>
            <button
              type="button"
              onClick={() =>
                setExpandedSection(
                  expandedSection === sectionKey ? null : sectionKey
                )
              }
              className="w-full bg-neutral-100 px-4 py-2.5 border-b border-neutral-200 flex items-center justify-between gap-2 hover:bg-neutral-200 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Icon icon={icon} className="w-3.5 h-3.5 text-neutral-500" />
                <span className="text-sm font-semibold text-neutral-700">
                  {title}
                </span>
                <span className="text-xs text-neutral-500">
                  ({items.length})
                </span>
              </div>
              <Icon
                icon={faChevronDown}
                className={`w-3 h-3 text-neutral-500 transition-transform duration-200 ${
                  expandedSection === sectionKey ? 'rotate-180' : ''
                }`}
              />
            </button>
            {expandedSection === sectionKey && (
              <div>
                {items.map((item) => {
                  const isSelected = selectedItems.some(
                    (s) => s.id === item.id
                  );
                  return (
                    <div
                      key={item.id}
                      onClick={() => onSelect(item)}
                      className={`p-4 border-b border-neutral-100 last:border-b-0 cursor-pointer transition-all duration-300 hover:bg-neutral-50 ${
                        isSelected ? 'bg-pink-50 hover:bg-pink-100' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-150 ${
                              isSelected
                                ? 'border-pink-600 bg-pink-600'
                                : 'border-neutral-300'
                            }`}
                          >
                            {isSelected && (
                              <Icon
                                icon={faCheck}
                                className="w-2.5 h-2.5 text-white"
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className={`font-medium text-sm truncate ${
                                isSelected
                                  ? 'text-pink-800'
                                  : 'text-neutral-900'
                              }`}
                            >
                              {stripHtml(item.label)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      };

      return (
        <div className="h-full overflow-y-auto border border-neutral-200 rounded-lg bg-white">
          {renderSection(
            'Company',
            faBuilding,
            groupedCodeSets.companyCodeSets,
            'company'
          )}
          {renderSection(
            'Personal',
            faUser,
            groupedCodeSets.personalCodeSets,
            'personal'
          )}
        </div>
      );
    },
    [groupedCodeSets, expandedSection]
  );

  // Create config with custom render function
  const modalConfigWithRender = useMemo(
    () => ({
      ...codeSetModalConfig,
      renderResultsSection: renderGroupedResults,
    }),
    [renderGroupedResults]
  );

  // Show loading state while hydrating data
  if (isHydrating) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 border border-neutral-200 rounded-full text-xs text-neutral-500">
        Loading code set...
      </span>
    );
  }

  // Show error state if hydration failed (e.g., code set not found)
  if (needsHydration && error) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 rounded-full text-xs text-red-600">
        Code set not found (ID: {codeSetId})
        <button
          className="ml-1 text-red-600 hover:text-red-800"
          onClick={() => onRemove(componentId)}
          aria-label="Remove invalid code set"
        >
          x
        </button>
      </span>
    );
  }

  return (
    <>
      {codeSets.length > 0 ? (
        <GenericNodeComponent
          config={codeSetNodeConfig}
          items={codeSets}
          connector="ALL"
          onRemoveItem={handleRemoveItem}
          onAddMore={() => setShowModal(true)}
        />
      ) : (
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-1 bg-pink-50 border border-pink-200 rounded-full cursor-pointer hover:bg-pink-100 transition-colors"
          onClick={() => setShowModal(true)}
        >
          <span className="text-pink-600 text-xs font-medium">
            Select code set...
          </span>
        </button>
      )}

      <GenericSelectorModal
        config={modalConfigWithRender}
        isOpen={showModal}
        selectedItems={codeSets}
        connector="ALL"
        isEditing={codeSets.length > 0}
        onClose={handleModalClose}
        onSave={handleSave}
        searchResults={searchResults}
        isLoading={isSearching}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </>
  );
};

export default CodeSetsComponent;

// Static method to render into a DOM element (for contentEditable integration)
export const renderCodeSetsComponent = (
  container: HTMLElement,
  props: CodeSetsComponentProps & {
    initialCodeSets?: CodeSetItem[];
    initialConnector?: ConnectorType;
  }
) => {
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <CodeSetsComponent {...props} />
    </Provider>
  );
  return root;
};
